import React, { useState, useRef, useEffect, createContext, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import "../../../assets/css/gcstaff/teachertopbar/TeacherTopbar.css";
import AppContext from "../../../context/AppContext";

/* =============================================
   TEACHER NOTIFICATION CONTEXT
   Shared between TeacherTopbar + TeacherNotificationsPage
   ============================================= */
const TeacherNotifContext = createContext(null);
export const useTeacherNotifContext = () => useContext(TeacherNotifContext);

/* ─── Notification Icon ─── */
const NotifIcon = ({ type }) => {
  const colorMap = {
    overdue: "gcTeacher-notif-icon--danger",
    book:    "gcTeacher-notif-icon--success",
    review:  "gcTeacher-notif-icon--info",
    dispute: "gcTeacher-notif-icon--warning",
  };
  const symbols = { overdue: "!", book: "✦", review: "★", dispute: "⚠" };
  return (
    <span className={`gcTeacher-notif-icon ${colorMap[type] || colorMap.review}`} aria-hidden="true">
      {symbols[type] || "●"}
    </span>
  );
};

/* ─── Relative time formatter ─── */
const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const now  = new Date();
  const then = new Date(dateStr);
  const diffSec = Math.floor((now - then) / 1000);

  if (diffSec < 60) return "Just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min${diffMin > 1 ? "s" : ""} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr > 1 ? "s" : ""} ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
};

/* ─── Notification Dropdown ─── */
const NotificationDropdown = ({
  notifications,
  setDropdownOpen,
  onMarkAllRead,
  onMarkOneRead,
  onDelete,
}) => {
  const navigate = useNavigate();
  const unreadCount = notifications.filter((n) => n.status === "unread").length;

  return (
    <div className="gcTeacher-notif-dropdown" role="dialog" aria-label="Notifications">

      {/* Head */}
      <div className="gcTeacher-notif-dropdown__head">
        <div className="gcTeacher-notif-dropdown__head-left">
          <span className="gcTeacher-notif-dropdown__title">Notifications</span>
          {unreadCount > 0 && (
            <span className="gcTeacher-notif-dropdown__unread-pill">{unreadCount} unread</span>
          )}
        </div>
        <button className="gcTeacher-notif-markall-btn" onClick={onMarkAllRead}>
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
            <path d="M2 6.5L5 9.5L11 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Mark all
        </button>
      </div>

      {/* List */}
      <ul className="gcTeacher-notif-list" role="list">
        {notifications.map((n) => (
          <li
            key={n.id}
            className={`gcTeacher-notif-item${n.status === "unread" ? " gcTeacher-notif-item--unread" : ""}`}
          >
            <NotifIcon type={n.type} />
            <div className="gcTeacher-notif-item__body">
              <div className="gcTeacher-notif-item__title-row">
                <span className="gcTeacher-notif-item__title">{n.title}</span>
                {n.status === "unread" && <span className="gcTeacher-notif-unread-dot" aria-label="unread" />}
              </div>
              <p className="gcTeacher-notif-item__summary">{n.subtitle}</p>
              <div className="gcTeacher-notif-item__footer">
                <span className="gcTeacher-notif-item__time">{timeAgo(n.created_at)}</span>
                <div className="gcTeacher-notif-item__actions">

                  {/* Show more → navigate to /teacher/notifications */}
                  <button
                    className="gcTeacher-notif-show-btn"
                    onClick={() => {
                      if (n.status === "unread") onMarkOneRead(n.id);
                      setDropdownOpen(false);
                      navigate("/teacher/notifications");
                    }}
                  >
                    Show more
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M3 2L6 5L3 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  {/* Mark as read */}
                  {n.status === "unread" && (
                    <button
                      className="gcTeacher-notif-icon-btn gcTeacher-notif-icon-btn--check"
                      onClick={() => onMarkOneRead(n.id)}
                      aria-label="Mark as read"
                    >
                      <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                        <path d="M2 6.5L5 9.5L11 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  )}

                  {/* Delete */}
                  <button
                    className="gcTeacher-notif-icon-btn gcTeacher-notif-icon-btn--del"
                    onClick={() => onDelete(n.id)}
                    aria-label="Delete notification"
                  >
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                      <path d="M2 3.5H11M5 3.5V2.5H8V3.5M4.5 3.5V10.5H8.5V3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                </div>
              </div>
            </div>
          </li>
        ))}

        {notifications.length === 0 && (
          <li className="gcTeacher-notif-empty">
            <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
              <path d="M17 4a9 9 0 019 9v5l2 3H6l2-3v-5A9 9 0 0117 4zM14 30a3 3 0 006 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            <span>No notifications</span>
          </li>
        )}
      </ul>

      {/* Footer */}
      <div className="gcTeacher-notif-dropdown__footer">
        <Link
          to="/teacher/notifications"
          className="gcTeacher-notif-view-all"
          onClick={() => setDropdownOpen(false)}
        >
          View all notifications
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
            <path d="M2.5 5.5H8.5M6 3L8.5 5.5L6 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link>
      </div>

    </div>
  );
};

/* ─── Main TeacherTopbar ─── */
const TeacherTopbar = ({ title = "Teacher Control Panel", collapsed }) => {
  const { baseUrl, loggedInUser, authToken } = useContext(AppContext);

  const [notifications, setNotifications] = useState([]);
  const [dropdownOpen, setDropdownOpen]   = useState(false);
  const wrapperRef = useRef(null);
  const bellRef    = useRef(null);

  const unreadCount = notifications.filter((n) => n.status === "unread").length;

  /* ── Fetch notifications from backend ── */
  const fetchNotifications = () => {
    if (!loggedInUser?.id) return;

    const formData = new FormData();
    formData.append("lbteacher_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/teacher/notifications/fetchAllNotifications`, formData, {
        headers: {
          "Content-Type": "multipart/formData",
          "Authorization": `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        if (res.data.success === true) {
          setNotifications(res.data.notifications);
        } else {
          setNotifications([]);
        }
      })
      .catch((err) => console.log(err));
  };

  /* ── Mark all as read ── */
  const handleMarkAllRead = () => {
    if (!loggedInUser?.id) return;

    const formData = new FormData();
    formData.append("lbteacher_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/teacher/notifications/markAllAsRead`, formData, {
        headers: {
          "Content-Type": "multipart/formData",
          "Authorization": `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        if (res.data.success === true) {
          setNotifications((prev) => prev.map((n) => ({ ...n, status: "read" })));
        }
      })
      .catch((err) => console.log(err));
  };

  /* ── Mark one as read ── */
  const handleMarkOneRead = (id) => {
    if (!loggedInUser?.id) return;

    const formData = new FormData();
    formData.append("id", id);
    formData.append("lbteacher_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/teacher/notifications/markOneAsRead`, formData, {
        headers: {
          "Content-Type": "multipart/formData",
          "Authorization": `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        if (res.data.success === true) {
          setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, status: "read" } : n))
          );
        }
      })
      .catch((err) => console.log(err));
  };

  /* ── Delete one notification ── */
  const handleDelete = (id) => {
    if (!loggedInUser?.id) return;

    const formData = new FormData();
    formData.append("id", id);
    formData.append("lbteacher_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/teacher/notifications/deleteNotification`, formData, {
        headers: {
          "Content-Type": "multipart/formData",
          "Authorization": `Bearer ${authToken}`,
        },
      })
      .then((res) => {
        if (res.data.success === true) {
          setNotifications((prev) => prev.filter((n) => n.id !== id));
        }
      })
      .catch((err) => console.log(err));
  };

  /* ── Initial fetch + poll every 30s ── */
  useEffect(() => {
    if (loggedInUser?.id) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser]);

  /* Close on outside click */
  useEffect(() => {
    const onOutside = (e) => {
      if (
        wrapperRef.current && !wrapperRef.current.contains(e.target) &&
        bellRef.current    && !bellRef.current.contains(e.target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const contextValue = { notifications, setNotifications };

  return (
    <TeacherNotifContext.Provider value={contextValue}>
      <header className={`gcTeacher-tb${collapsed ? " gcTeacher-tb--collapsed" : ""}`}>
        <span className="gcTeacher-tb__title">{title}</span>

        <div className="gcTeacher-tb__right">

          {/* Bell */}
          <button
            ref={bellRef}
            className={`gcTeacher-tb__bell-btn${dropdownOpen ? " gcTeacher-tb__bell-btn--active" : ""}`}
            onClick={() => setDropdownOpen((p) => !p)}
            aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ""}`}
            aria-expanded={dropdownOpen}
          >
            <svg className="gcTeacher-tb__bell-svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M10 2.5a6 6 0 016 6v3.5l1.5 2H2.5L4 12V8.5a6 6 0 016-6zM8 16.5a2 2 0 004 0"
                stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
              />
            </svg>
            {unreadCount > 0 && (
              <span className="gcTeacher-tb__badge" aria-hidden="true">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {/* Dropdown */}
          {dropdownOpen && (
            <div className="gcTeacher-notif-panel" ref={wrapperRef}>
              <NotificationDropdown
                notifications={notifications}
                setDropdownOpen={setDropdownOpen}
                onMarkAllRead={handleMarkAllRead}
                onMarkOneRead={handleMarkOneRead}
                onDelete={handleDelete}
              />
            </div>
          )}

        </div>
      </header>
    </TeacherNotifContext.Provider>
  );
};

export default TeacherTopbar;